import { useState } from 'react';
import { Dialog } from '../../components/Dialog.tsx';
import type { DuplicateAction } from '../../media/duplicates.ts';
import { useDuplicatePrompt } from '../../state/duplicate-prompt.ts';

function formatSize(bytes: number | undefined): string {
  if (bytes === undefined) return 'unknown size';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

/** Asks what to do with an imported file that is already in the library. */
export function DuplicateDialog() {
  const question = useDuplicatePrompt((s) => s.question);
  const answer = useDuplicatePrompt((s) => s.answer);
  const [applyToAll, setApplyToAll] = useState(false);
  if (!question) return null;

  const { fileName, size, existing, identical, remaining } = question;
  const choose = (action: DuplicateAction) => {
    answer({ action, applyToAll: remaining > 0 && applyToAll });
    setApplyToAll(false);
  };

  return (
    <Dialog
      title="Already in the library"
      onClose={() => choose('keep')}
      width={460}
      footer={
        <>
          {remaining > 0 && (
            <label className="checkbox-label duplicate-all">
              <input type="checkbox" checked={applyToAll} onChange={(e) => setApplyToAll(e.target.checked)} />
              Do this for the other {remaining} duplicate{remaining === 1 ? '' : 's'}
            </label>
          )}
          <span className="spacer" />
          <button className="btn" onClick={() => choose('keep')}>
            Keep existing
          </button>
          <button className="btn" onClick={() => choose('add')}>
            Add anyway
          </button>
          <button className="btn primary" onClick={() => choose('replace')} autoFocus>
            Replace
          </button>
        </>
      }
    >
      <p>
        <strong>{fileName}</strong>{' '}
        {identical ? 'looks like the same file as' : `(${formatSize(size)}) has the same name as`}{' '}
        <strong>{existing.name}</strong>
        {!identical && ` (${formatSize(existing.location.size)})`} in the library.
      </p>
      <ul className="duplicate-help muted">
        <li>
          <strong>Replace</strong> — use the new file; clips already on the timeline switch to it.
        </li>
        <li>
          <strong>Keep existing</strong> — don’t import it; the library item is used instead.
        </li>
        <li>
          <strong>Add anyway</strong> — import it as a separate item.
        </li>
      </ul>
    </Dialog>
  );
}
